import { css, cx } from '@linaria/core'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { extractTitle } from '#features/Configuration/ConfigurationTitle'
import DetailsIcon from '#icons/info-circle-outline-48.svg'
import DeliveryDateIcon from '#icons/package-carry-24.svg'
import { type ConfigurationStatus, StatusIcon } from '#pages/Configuration/components/ConfigurationStatusIcon'
import { fragments, style } from '#styles/index'
import { type DateString, formatPartPeriod as formatPartPeriodExt, isValidDateString, stringToPeriod } from '#utils/DateString'
import { formatDateTime } from '#utils/formatDateTime'
import { useShowManCodeInformationOverlay } from './useShowManCodeInformationOverlay'

// 1 day: 24hr * 60min * 60s * 1000ms
const dayTime = 24 * 60 * 60 * 1000
const timeCutoff = [
  {
    // 1 day: 24hr * 60min * 60s * 1000ms
    cutoff: dayTime,
    type: 'day',
  },
  {
    cutoff: 7 * dayTime,
    type: 'week',
  },
  {
    cutoff: 30 * dayTime,
    type: 'month',
  },
  {
    cutoff: Number.MAX_VALUE,
    type: 'ancient',
  },
] as const
type BucketKey = (typeof timeCutoff)[number]['type']

const clusterHistory = (manCodes: ManCodeTableEntry[]) => {
  const timeBuckets: Record<BucketKey, ManCodeTableEntry[]> = {
    day: [],
    week: [],
    month: [],
    ancient: [],
  }
  const now = Date.now()
  for (const manCode of manCodes) {
    const timeDelta = now - (manCode.updatedAt ?? manCode.createdAt ?? manCode.timestamp ?? 0)
    for (const { cutoff, type } of timeCutoff) {
      if (cutoff > timeDelta) {
        timeBuckets[type].push(manCode)
        break
      }
    }
  }
  for (const bucket of Object.values(timeBuckets)) {
    bucket.sort((a, b) => {
      const aTime = a.updatedAt ?? a.createdAt ?? a.timestamp ?? 0
      const bTime = b.updatedAt ?? b.createdAt ?? b.timestamp ?? 0
      return bTime - aTime
    })
  }
  const timeTable = [
    { type: 'day', codes: timeBuckets.day },
    { type: 'week', codes: timeBuckets.week },
    { type: 'month', codes: timeBuckets.month },
    { type: 'ancient', codes: timeBuckets.ancient },
  ] as const
  return timeTable.filter((x) => x.codes.length > 0)
}

const manCodeTableClass = css`
  display: grid;
  grid-template-columns: 1fr min-content min-content max-content  max-content;
  min-width: max-content;
  flex: 1;
  padding-bottom: 8px;
    
  > ._group {
    display: grid;
    grid-column: 1/-1;
    grid-template-columns: subgrid;

    > ._title {
      grid-column: 1 / -1;
      padding: 15px 0 2px 0;
      text-transform: uppercase;
      color: ${style.themeColors.textDeselected};
      ${fragments.textStyle.body.s.regular};
    }
  
    > ._row {
      grid-column: 1 / -1;
      grid-template-columns: subgrid;
      display: grid;
      border-bottom: 1px solid ${style.themeColors.lineDefault};

      &:hover {
        > ._code, > ._naming, > ._period, >._selection-slot {
          background-color: ${style.themeColors.backgroundBoxedHover};
        }
      }
 
      > ._code, > ._naming {
        padding-right: 16px;
      }
      
      > ._details {
        width: 56px;
      }
    
      > ._code, > ._naming, > ._period, >._selection-slot {
        background-color: transparent;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 5px 8px 7px 8px;
        flex: 1;
        cursor: pointer;
        transition: 0.25s ease background-color;
        gap: 2px;

      
        > * {
          word-break: keep-all;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        > ._man-code, >._market-denomination, >._period {
          ${fragments.textStyle.body.s.regular};
        }
  
        > ._value, >._title, >._delivery-date {
          ${fragments.textStyle.body.m.bold};
        }

        > ._delivery-date {
          display: flex;
          align-items: center;
          gap: 2px;
        }
      }

      > ._code {
        flex-direction: row;
        gap: 8px;
        > ._code, >._status {
          display: flex;
          flex-direction: column;
          gap: 2px;
          justify-content: center;
        }
        > ._status {
          align-items: center;

          > ._index {
            ${fragments.textStyle.body.m.bold};
          }
        }

        > ._code > ._title {
          ${fragments.textStyle.body.m.bold};

          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

      }
  
      > ._period {
        align-items: flex-end;
      }
  
      > ._info-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
      }
    }
  }
`

const formatPartPeriod = (period: DateString | undefined) => (isValidDateString(period) ? formatPartPeriodExt(stringToPeriod(period)) : '—')

export interface ManCodeTableEntry {
  index?: number
  status?: ConfigurationStatus
  manCode?: EnhancedManCodeId
  title?: string
  marketDenomination?: string
  timestamp?: Timestamp
  createdAt?: Timestamp
  updatedAt?: Timestamp
  deliveryDate?: DateString
  partPeriod?: DateString
}

export const ManCodeTable: FC<{
  entries: ManCodeTableEntry[]
  withCluster?: boolean
  onManCodeSelected: (selection: ManCodeTableEntry) => void
  InfoSlot?: FC<{ code: ManCodeTableEntry }>
  SelectionSlot?: FC<{ code: ManCodeTableEntry }>
  className?: string
}> = ({ entries, withCluster, onManCodeSelected, InfoSlot, SelectionSlot, className }) => {
  const { i18n, t } = useTranslation()
  const orderedHistory = useMemo(() => {
    if (withCluster) {
      return clusterHistory(entries)
    } else {
      const sortedCodes = [...entries].sort((a, b) => {
        const aTime = a.updatedAt ?? a.createdAt ?? a.timestamp ?? 0
        const bTime = b.updatedAt ?? b.createdAt ?? b.timestamp ?? 0
        return bTime - aTime
      })
      return [{ type: 'without', codes: sortedCodes }] as const
    }
  }, [entries, withCluster])

  const translations = useMemo(
    () =>
      ({
        day: t('code-table.duration.today', 'Today'),
        week: t('code-table.duration.week', 'Last 7 days'),
        month: t('code-table.duration.month', 'Last 30 days'),
        ancient: t('code-table.duration.ancient', 'Long time ago'),
        without: undefined,
      }) as const,
    [t],
  )

  const showManCode = useShowManCodeInformationOverlay()

  return (
    <div className={cx(manCodeTableClass, className)}>
      {orderedHistory.map(({ type, codes }) => {
        return (
          <div key={type} className="_group">
            {
              // display only visible if the history is clustered by date
              type !== 'without' && <h3 className="_title">{translations[type]}</h3>
            }
            {codes.map((tableEntry) => {
              const { configurationTitle } = extractTitle(tableEntry.title)

              return (
                <div
                  className="_row"
                  key={tableEntry.manCode}
                  onClick={() => {
                    onManCodeSelected(tableEntry)
                  }}
                >
                  <div className="_code">
                    {(tableEntry.index || tableEntry.status) && (
                      <div className="_status">
                        <span className="_index">{tableEntry.index}</span>
                        <span className="_status">{tableEntry.status && <StatusIcon status={tableEntry.status} />}</span>
                      </div>
                    )}
                    <div className="_code">
                      <span className="_title" title={configurationTitle}>
                        {configurationTitle}
                      </span>
                      <span className="_timestamp">
                        {formatDateTime(i18n, tableEntry.updatedAt ?? tableEntry.createdAt ?? tableEntry.timestamp).split(',')[0]}
                      </span>
                    </div>
                  </div>
                  <div className="_naming">
                    <span className="_value">{tableEntry.manCode ?? '—'}</span>
                    <span className="_market-denomination">{tableEntry.marketDenomination ?? '—'}</span>
                  </div>
                  <div className="_period">
                    <span className="_delivery-date">
                      {tableEntry.deliveryDate ? (
                        <>
                          <Icon iconUrl={DeliveryDateIcon} size="xs" />
                          {tableEntry.deliveryDate}
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                    <span className="_part-period">{formatPartPeriod(tableEntry.partPeriod)}</span>
                  </div>
                  {SelectionSlot && (
                    <div className="_selection-slot">
                      <SelectionSlot code={tableEntry} />
                    </div>
                  )}
                  {tableEntry.manCode && (
                    <IconButton
                      className="_details"
                      iconSize="l"
                      iconUrl={DetailsIcon}
                      title={t('code-table.actions.show-code-details', 'Show Code Details')}
                      onClick={(evt) => {
                        evt.preventDefault()
                        evt.stopPropagation()
                        showManCode(tableEntry.manCode, tableEntry.title ?? tableEntry.marketDenomination)
                      }}
                    />
                  )}
                  {InfoSlot && (
                    <div className="_info-slot">
                      <InfoSlot code={tableEntry} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
